'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useConfig } from './ConfigProvider';

/** Liens internes de la vitrine, dans l'ordre d'affichage. */
const PAGES = [
  { href: '/pricing', label: 'Tarifs' },
  { href: '/docs', label: 'Documentation' },
  { href: '/resources', label: 'Ressources' },
];

const LINK_BASE =
  'rounded-full px-3 py-2 text-sm font-medium transition-colors duration-150';
const LINK_IDLE =
  'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white';
const LINK_ACTIVE =
  'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300';

const MOBILE_LINK =
  'block rounded-xl px-4 py-3 text-base font-medium transition-colors duration-150';

function ExternalIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5 opacity-60"
    >
      <path d="M7.5 4.5h8v8M15.5 4.5 8 12" />
      <path d="M13 12.5v3h-9v-9h3" />
    </svg>
  );
}

export function PublicNavbar() {
  const { appUrl: APP_URL, comusliveUrl: COMUSLIVE_URL } = useConfig();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  // La barre reste translucide en haut de page (elle se fond dans le hero)
  // et gagne son fond opaque + ombre dès qu'on défile.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Une navigation interne ne démonte pas la navbar : on referme à la main.
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  return (
    <nav
      className={`sticky top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? 'border-b border-slate-200/80 bg-white/85 shadow-sm shadow-slate-900/5 backdrop-blur-xl dark:border-slate-700/70 dark:bg-slate-900/85'
          : 'border-b border-transparent bg-white/70 backdrop-blur-md dark:bg-slate-900/70'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          href="/"
          className="group flex items-center gap-2.5 text-lg font-bold tracking-tight text-slate-900 dark:text-white"
        >
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-base shadow-lg shadow-blue-600/25 transition-transform duration-200 group-hover:scale-105">
            🎵
          </span>
          ComusFlow
        </Link>

        <button
          type="button"
          className="-mr-1 grid h-10 w-10 place-items-center rounded-xl text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800 lg:hidden"
          aria-expanded={open}
          aria-controls="publicNav"
          aria-label="Menu"
          onClick={() => setOpen(!open)}
        >
          <span className="relative block h-4 w-6">
            <span
              className={`absolute left-0 block h-0.5 w-6 rounded-full bg-current transition-all duration-300 ${
                open ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0'
              }`}
            />
            <span
              className={`absolute left-0 top-1/2 block h-0.5 w-6 -translate-y-1/2 rounded-full bg-current transition-opacity duration-200 ${
                open ? 'opacity-0' : 'opacity-100'
              }`}
            />
            <span
              className={`absolute left-0 block h-0.5 w-6 rounded-full bg-current transition-all duration-300 ${
                open ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-0'
              }`}
            />
          </span>
        </button>

        {/* Desktop */}
        <div className="hidden items-center lg:flex">
          <a
            href={COMUSLIVE_URL}
            className={`${LINK_BASE} ${LINK_IDLE} inline-flex items-center gap-1.5`}
          >
            Événements
            <ExternalIcon />
          </a>
          {PAGES.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              aria-current={isActive(href) ? 'page' : undefined}
              className={`${LINK_BASE} ${isActive(href) ? LINK_ACTIVE : LINK_IDLE}`}
            >
              {label}
            </Link>
          ))}
          {/* Le formulaire vit dans l'app (préremplissage si session, design
              system partagé) : la vitrine y renvoie, elle ne le duplique pas. */}
          <a href={`${APP_URL}/contact`} className={`${LINK_BASE} ${LINK_IDLE}`}>
            Nous contacter
          </a>

          <span
            aria-hidden="true"
            className="mx-3 h-5 w-px bg-slate-200 dark:bg-slate-700"
          />

          <a
            href={`${APP_URL}/admin/login`}
            className={`${LINK_BASE} ${LINK_IDLE}`}
          >
            Se connecter
          </a>
          <a
            href={`${APP_URL}/admin/auth/signup`}
            className="ml-2 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:shadow-xl hover:shadow-blue-600/30 hover:brightness-110"
          >
            S&apos;inscrire
          </a>
        </div>
      </div>

      {/* Mobile : panneau flottant détaché de la barre */}
      <div
        id="publicNav"
        className={`absolute inset-x-0 top-full px-4 pb-4 transition-all duration-200 ease-out sm:px-6 lg:hidden ${
          open
            ? 'visible translate-y-0 opacity-100'
            : 'invisible -translate-y-2 opacity-0'
        }`}
      >
        <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-900/10 dark:border-slate-700 dark:bg-slate-800 dark:shadow-black/40">
          <a
            href={COMUSLIVE_URL}
            className={`${MOBILE_LINK} ${LINK_IDLE} flex items-center justify-between`}
          >
            Événements
            <ExternalIcon />
          </a>
          {PAGES.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              aria-current={isActive(href) ? 'page' : undefined}
              className={`${MOBILE_LINK} ${isActive(href) ? LINK_ACTIVE : LINK_IDLE}`}
            >
              {label}
            </Link>
          ))}
          <a href={`${APP_URL}/contact`} className={`${MOBILE_LINK} ${LINK_IDLE}`}>
            Nous contacter
          </a>

          <div className="my-2 h-px bg-slate-200 dark:bg-slate-700" />

          <a
            href={`${APP_URL}/admin/login`}
            className={`${MOBILE_LINK} ${LINK_IDLE}`}
          >
            Se connecter
          </a>
          <a
            href={`${APP_URL}/admin/auth/signup`}
            className="mt-1 flex items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3 text-base font-semibold text-white shadow-lg shadow-blue-600/25"
          >
            S&apos;inscrire
          </a>
        </div>
      </div>
    </nav>
  );
}
